import { useMemo, useState } from "react";
import { Grid2, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { useTranslate } from "../../../../../hooks/useTranslate";
import TextInput from "../../../../../components/TextInput";
import { useSchoolAddressForm } from "../../hooks/useSchoolAddressForm";
import DatePickerInput from "../../../../../components/DatePickerInput";
import Preloader from "../../../../../components/Preloader";
import FileDropUploader from "../../../../../components/FileDropUploader";
import Select from "../../../../../components/Select";
import { isVersionUK, isVersionUS } from "../../../../../services/versionService";
import TranscriptLetterTemplate
    from "../../../../../assets/docs/King's InterHigh Email Template for Previous Education Records Request.docx";
import TranscriptLetterTemplateUSPA
    from "../../../../../assets/docs/USPA Live - Email Template for Previous Education Records Request.docx";
import "./SchoolAddressForm.css";
import { getVerificationStatusKeyAndFlag, KEY_NONE } from "../../../../../utils/getVerificationStatusKey";
import VerificationStatusChip from "../../../../../components/VerificationStatusChip";
import CountrySelect from "../../../../../components/CountrySelect";
import useStatuses from "../../../../../context/status/useStatuses";
import { useGetReasonsForJoining } from "../../../../../hooks/useGetReasonsForJoining";
import { addOneDay } from "../../../../../utils/dateTimeHelper";

const SchoolAddressForm = (props) => {
    const {
        data,
        isSaving,
        isSubmitted: isSubmittedProp,
        onSave,
        isDisabled,
    } = props;

    const { t } = useTranslate();
    const { generalStatuses } = useStatuses();
    const { studyingIb, yearGroup = 0 } = generalStatuses?.student || {};
    const { reasonsOptions } = useGetReasonsForJoining();

    const isSchoolReportsRequired = isVersionUK && ([11, 13].includes(yearGroup) || !!studyingIb);
    const isOtherFilesRequired = isVersionUK && !!studyingIb;

    const [transcripts, setTranscripts] = useState([]);
    const [applicationLetter, setApplicationLetter] = useState([]);
    const [mockResults, setMockResults] = useState([]);
    const transcriptStatus = useMemo(() => getVerificationStatusKeyAndFlag({
        document: data?.transcript,
        verificationStatus: data?.transcriptStatus,
    }), [data?.transcript, data?.transcriptStatus]);

    const uploadedTranscript = useMemo(() => (data?.transcript ? [data.transcript] : null), [data.transcript]);
    const uploadedApplicationLetter = useMemo(() => (data?.ibApplicationLetter ? [data.ibApplicationLetter] : null), [data.ibApplicationLetter]);
    const uploadedGradeEvidence = useMemo(() => (data?.ibGradeEvidence ? [data.ibGradeEvidence] : null), [data.ibGradeEvidence]);

    const {
        register, control, errors,
        handleSubmit, isSubmitted: isSubmittedForm,
        previousInstitutionFrom,
    } = useSchoolAddressForm(data);

    const minToDate = previousInstitutionFrom ? addOneDay(previousInstitutionFrom) : undefined;
    const isSubmitted = isSubmittedForm || isSubmittedProp;

    const onSubmit = handleSubmit((formData) => {
        const {
            addressLine1,
            addressLine2,
            city,
            area,
            code,
            country,
            ...rest
        } = formData;
        const previousInstitutionAddress = {
            addressLine1, addressLine2, city, area, code, country,
        };
        onSave?.({
            previousInstitutionAddress, ...rest, transcripts, applicationLetter, mockResults,
        });
    });
    const onChangeTranscripts = (files) => {
        setTranscripts(files);
    };
    const today = new Date();
    const twoYearsLater = new Date(today.setFullYear(today.getFullYear() + 2));

    return (
        <Preloader isLoading={isSaving} overflow showInitiallyOverflow>
            <form onSubmit={onSubmit}>
                <Grid2 className="school-address-form" container spacing={4}>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <Typography variant="h4" component="div" className="text-primary">
                            {t("previousEducation.form.previousInstitutionDetails")}
                        </Typography>
                        <Typography variant="body1" component="div" className="mt-4">
                            {t("previousEducation.form.previousInstitutionDetailsSubtitle")}
                        </Typography>
                        <Grid2 size={12} className="mt-8 mb-4">
                            <TextInput
                                label={t("previousEducation.form.previousInstitutionName")}
                                register={register}
                                name="previousInstitutionName"
                                error={errors.previousInstitutionName?.message}
                                disabled={isDisabled}
                            />
                        </Grid2>
                        <Grid2 container spacing={2}>
                            <Grid2 size={{ xs: 12, md: 6 }} className="mb-4">
                                <TextInput
                                    label={t("previousEducation.form.addressLine1")}
                                    register={register}
                                    name="addressLine1"
                                    error={errors.addressLine1?.message}
                                    disabled={isDisabled}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 6 }} className="mb-4">
                                <TextInput
                                    label={t("previousEducation.form.addressLine2")}
                                    register={register}
                                    name="addressLine2"
                                    error={errors.addressLine2?.message}
                                    disabled={isDisabled}
                                />
                            </Grid2>
                        </Grid2>
                        <Grid2 container spacing={2}>
                            <Grid2 size={{ xs: 12, md: 6 }} className="mb-4">
                                <TextInput
                                    label={t("previousEducation.form.city")}
                                    register={register}
                                    name="city"
                                    error={errors.city?.message}
                                    disabled={isDisabled}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 6 }} className="mb-4">
                                <TextInput
                                    label={t("previousEducation.form.zip")}
                                    register={register}
                                    name="code"
                                    error={errors.code?.message}
                                    disabled={isDisabled}
                                />
                            </Grid2>
                        </Grid2>
                        <Grid2 container spacing={2}>
                            <Grid2 size={{ xs: 12, md: 6 }} className="mb-4">
                                <TextInput
                                    label={t("previousEducation.form.state")}
                                    register={register}
                                    name="area"
                                    error={errors.area?.message}
                                    disabled={isDisabled}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 6 }} className="mb-4">
                                <CountrySelect
                                    label={t("previousEducation.form.countryOfResidence")}
                                    control={control}
                                    name="country"
                                    error={errors.country?.message}
                                    disabled={isDisabled}
                                />
                            </Grid2>
                        </Grid2>
                        <Grid2 container spacing={2}>
                            <Grid2 size={{ xs: 12, md: 6 }} className="mb-4">
                                <DatePickerInput
                                    label={t("previousEducation.form.attendedDate")}
                                    control={control}
                                    name="previousInstitutionFrom"
                                    error={errors.previousInstitutionFrom?.message}
                                    disabled={isDisabled}
                                    disableFuture
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 6 }} className="mb-4">
                                <DatePickerInput
                                    label={t("previousEducation.form.leaveDate")}
                                    control={control}
                                    maxDate={twoYearsLater}
                                    name="previousInstitutionTo"
                                    minDate={minToDate}
                                    error={errors.previousInstitutionTo?.message}
                                    disabled={isDisabled}
                                />
                            </Grid2>
                        </Grid2>
                        {isVersionUK && (
                            <Grid2 size={12} className="mb-4">
                                <Select
                                    label={t("previousEducation.form.joinReason")}
                                    register={register}
                                    control={control}
                                    name="reasonForJoining"
                                    error={errors.reasonForJoining?.message}
                                    disabled={isDisabled}
                                    options={[{ value: "", label: t("previousEducation.form.selectReason"), disabled: true }, ...reasonsOptions]}
                                />
                            </Grid2>
                        )}
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <div className="transcripts-upload">
                            <Typography variant="h4" component="div" className="text-primary">
                                {t("previousEducation.transcripts.transcripts")}
                            </Typography>
                            {(isSchoolReportsRequired && transcriptStatus.key !== KEY_NONE) && (
                                <div className="mb-4">
                                    <VerificationStatusChip status={transcriptStatus} />
                                </div>
                            )}
                            <Typography variant="body1" component="div" className="mt-4">
                                {isSchoolReportsRequired ? t("previousEducation.transcripts.transcriptsSubtitleMandatory") : t("previousEducation.transcripts.transcriptsSubtitle")}
                            </Typography>
                            {isVersionUS && (
                                <a
                                    href={isVersionUK ? TranscriptLetterTemplate : TranscriptLetterTemplateUSPA}
                                    className="transcripts-upload__download mt-8 text-link"
                                >
                                    {t("previousEducation.transcripts.downloadOurTemplate")}
                                </a>
                            )}
                            <FileDropUploader
                                accept={{
                                    "image/jpeg": [".png", ".jpg", ".jpeg"],
                                    "application/pdf": [".pdf"],
                                    "application/word": [".doc", ".docx", ".odt", ".rtf", ".txt"],
                                }}
                                className="mt-4"
                                uploadedFiles={uploadedTranscript}
                                showFiles
                                onChange={onChangeTranscripts}
                                disabled={isDisabled || transcriptStatus.isInfo || transcriptStatus.isSuccess}
                                error={!(transcriptStatus.isInfo || transcriptStatus.isSuccess) && isSubmitted && isSchoolReportsRequired && transcripts.length === 0}
                            />
                            {(!(transcriptStatus.isInfo || transcriptStatus.isSuccess) && isSubmitted && isSchoolReportsRequired && transcripts.length === 0) && (
                                <Typography variant="body1" component="div" className="mt-2 text-base text-error">
                                    {t("formErrors.required")}
                                </Typography>
                            )}
                            {isOtherFilesRequired && (
                                <>
                                    <div className="mt-8">
                                        <Typography variant="h4" component="div" className="text-primary">
                                            {t("previousEducation.applicationLetter.title")}
                                        </Typography>
                                        <Typography variant="body1" component="div" className="mt-4">
                                            {t("previousEducation.applicationLetter.subtitle")}
                                        </Typography>
                                        <FileDropUploader
                                            accept={{
                                                "image/jpeg": [".png", ".jpg", ".jpeg"],
                                                "application/pdf": [".pdf"],
                                                "application/word": [".doc", ".docx", ".odt", ".rtf", ".txt"],
                                            }}
                                            className="mt-4"
                                            uploadedFiles={uploadedApplicationLetter}
                                            showFiles
                                            onChange={setApplicationLetter}
                                            disabled={isDisabled}
                                            error={isSubmitted && isOtherFilesRequired && applicationLetter.length === 0}
                                        />
                                        {(isSubmitted && isOtherFilesRequired && applicationLetter.length === 0) && (
                                            <Typography
                                                variant="body1"
                                                component="div"
                                                className="mt-2 text-base text-error"
                                            >
                                                {t("formErrors.required")}
                                            </Typography>
                                        )}
                                    </div>
                                    <div className="mt-8">
                                        <Typography variant="h4" component="div" className="text-primary">
                                            {t("previousEducation.mockResults.title")}
                                        </Typography>
                                        <Typography variant="body1" component="div" className="mt-4">
                                            {t("previousEducation.mockResults.subtitle")}
                                        </Typography>
                                        <FileDropUploader
                                            accept={{
                                                "image/jpeg": [".png", ".jpg", ".jpeg"],
                                                "application/pdf": [".pdf"],
                                                "application/word": [".doc", ".docx", ".odt", ".rtf", ".txt"],
                                            }}
                                            className="mt-4"
                                            uploadedFiles={uploadedGradeEvidence}
                                            showFiles
                                            onChange={setMockResults}
                                            disabled={isDisabled}
                                            error={isSubmitted && isOtherFilesRequired && mockResults.length === 0}
                                        />
                                        {(isSubmitted && isOtherFilesRequired && mockResults.length === 0) && (
                                            <Typography
                                                variant="body1"
                                                component="div"
                                                className="mt-2 text-base text-error"
                                            >
                                                {t("formErrors.required")}
                                            </Typography>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </Grid2>
                </Grid2>
                <Button
                    variant="contained"
                    className="button-accent mt-4 w-full"
                    type="submit"
                    loading={isSaving}
                    disabled={isDisabled}
                >
                    {t("previousEducation.form.submit")}
                </Button>
            </form>
        </Preloader>
    );
};

export default SchoolAddressForm;
